import { useEffect } from "react";
import { post } from "./api";

type ActivityReply = { recorded: boolean; retry_after_seconds: number };
type Input = Pick<Event, "type" | "isTrusted">;
const INPUT_TYPES = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart", "touchmove"] as const;
const COOLDOWN_MS = 30 * 60_000;
const MAX_AGE_MS = 60 * 60_000;

/** Timers only deliver a previously observed input; they never create activity. */
export function createActivityReporter(send: (idleSeconds: number) => Promise<ActivityReply>, now = () => performance.now()) {
  let pending: { monotonic: number; wall: number } | undefined;
  let nextAttempt = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let inFlight = false;
  let stopped = false;
  let retryDelay = 60_000;

  const schedule = () => {
    if (stopped || inFlight || pending === undefined || timer !== undefined) return;
    timer = setTimeout(() => { timer = undefined; void flush(); }, Math.max(0, nextAttempt - now()));
  };
  const flush = async () => {
    if (stopped || inFlight || pending === undefined) return;
    // Some platforms pause performance.now() during suspend. A forward wall
    // clock gap may only age/drop the retained input, never make it fresher.
    const age = Math.max(now() - pending.monotonic, Date.now() - pending.wall);
    if (age < 0 || age > MAX_AGE_MS) { pending = undefined; return; }
    const sentInput = pending;
    inFlight = true;
    try {
      const reply = await send(Math.floor(age / 1000));
      if (stopped) return;
      if (pending === sentInput && reply.recorded) pending = undefined;
      const seconds = Number.isFinite(reply.retry_after_seconds) ? reply.retry_after_seconds : 1800;
      nextAttempt = now() + (seconds > 0 ? Math.min(COOLDOWN_MS, seconds * 1000) : COOLDOWN_MS);
      retryDelay = 60_000;
    } catch {
      if (stopped) return;
      nextAttempt = now() + retryDelay;
      retryDelay = Math.min(COOLDOWN_MS, retryDelay * 2);
    } finally {
      inFlight = false;
      schedule();
    }
  };
  return {
    input(event: Input, visible: boolean, focused: boolean) {
      if (stopped || !event.isTrusted || !visible || !focused || !INPUT_TYPES.includes(event.type as typeof INPUT_TYPES[number])) return;
      pending = { monotonic: now(), wall: Date.now() };
      if (!nextAttempt) nextAttempt = pending.monotonic + 1000;
      schedule();
    },
    stop() { stopped = true; pending = undefined; if (timer !== undefined) clearTimeout(timer); },
  };
}

export function useSessionActivity(sessionId?: string) {
  useEffect(() => {
    if (!sessionId) return;
    const reporter = createActivityReporter(idle_seconds => post<ActivityReply>("/api/v1/auth/activity", { idle_seconds }));
    const input = (event: Event) => reporter.input(event, document.visibilityState === "visible", document.hasFocus());
    for (const type of INPUT_TYPES) window.addEventListener(type, input, { capture: true, passive: true });
    return () => {
      reporter.stop();
      for (const type of INPUT_TYPES) window.removeEventListener(type, input, true);
    };
  }, [sessionId]);
}
